import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Text, XStack, YStack } from 'tamagui'

import { api } from '../../../api/client'
import { useApp } from '../../../i18n'
import type { RequestCard } from '../../../lib/types'
import { tone } from '../../../theme/tones'
import { Button } from '../../../ui'
import { useMe } from '../../users/useUsers'
import { errorText } from '../errorText'
import { useDecideTask } from '../useEdo'
import { ItWorksForm } from './ItWorksForm'

/**
 * Черновик IT_WORKS правит и удаляет его инициатор, пока заявку не подали (процесса нет). Поля у
 * черновика могут отсутствовать — читаем защитно. Отзыва у черновика нет: его удаляют.
 */
export function canEditItWorksDraft(card: RequestCard, meId?: string): boolean {
  return !!meId && card.type === 'IT_WORKS' && card.status === 'DRAFT' &&
    (card.initiator?.id ?? card.initiatorId) === meId && !card.processInstanceId
}

/** Черновик в теле карточки: «Открыть черновик» (форма с данными черновика) и «Удалить черновик». */
export function ItWorksActions({ card, onDone }: { card: RequestCard; onDone: () => void }) {
  const { t } = useApp()
  const qc = useQueryClient()
  const meId = useMe().data?.id
  const [open, setOpen] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const del = useMutation({
    mutationFn: () => api.deleteItWorksDraft(card.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      qc.invalidateQueries({ queryKey: ['edo-participating'] })
      // Карточки больше нет: пометить устаревшей, но не перечитывать (404 до закрытия панели).
      qc.invalidateQueries({ queryKey: ['edo-request', card.id], refetchType: 'none' })
      onDone()
    },
  })
  if (!canEditItWorksDraft(card, meId)) return null
  return (
    <YStack gap={10}>
      {open ? (
        <ItWorksForm card={card} onDone={onDone} onCancel={() => setOpen(false)} />
      ) : (
        <Button onPress={() => setOpen(true)}>{t({ ru: 'Открыть черновик', en: 'Open draft', az: 'Qaralamanı aç' })}</Button>
      )}
      {confirming ? (
        <YStack gap={8} padding={10} borderRadius={7} borderWidth={1} borderColor="$borderColor" backgroundColor="$bgLayout">
          <Text fontSize={13} color="$color">
            {t({ ru: 'Удалить черновик? Восстановить его будет нельзя.', en: 'Delete the draft? This cannot be undone.', az: 'Qaralama silinsin? Bunu geri qaytarmaq mümkün olmayacaq.' })}
          </Text>
          <XStack gap={8} flexWrap="wrap">
            <Button size="sm" disabled={del.isPending} onPress={() => setConfirming(false)}>{t({ ru: 'Отмена', en: 'Cancel', az: 'Ləğv et' })}</Button>
            <Button size="sm" variant="primary" disabled={del.isPending || del.isSuccess} onPress={() => del.mutate()}>
              {del.isPending ? t({ ru: 'Удаление…', en: 'Deleting…', az: 'Silinir…' }) : t({ ru: 'Удалить', en: 'Delete', az: 'Sil' })}
            </Button>
          </XStack>
        </YStack>
      ) : (
        <Button onPress={() => setConfirming(true)}>{t({ ru: 'Удалить черновик', en: 'Delete draft', az: 'Qaralamanı sil' })}</Button>
      )}
      {del.error instanceof Error && <Text fontSize={12} color={tone('red').text}>{t(errorText(del.error))}</Text>}
    </YStack>
  )
}

/**
 * Доработка IT_WORKS (шаг REWORK): форма в режиме revise вместо общей панели решения. Переотправка —
 * общий decide: в `payload` ТОЛЬКО `itWorks` (любой другой ключ сервер отбивает «Эти поля правке не
 * подлежат»), `uploadIds` — только файлы, загруженные в этой правке. Отдельного комментария к
 * решению нет: комментарий инициатора — поле самой формы.
 */
export function ItWorksRework({ card, taskId, onDone, onCancel }: { card: RequestCard; taskId: string; onDone: () => void; onCancel: () => void }) {
  const decide = useDecideTask()
  return (
    <ItWorksForm
      mode="revise"
      card={card}
      onDone={onDone}
      onCancel={onCancel}
      onRevise={(input, newUploadIds) =>
        decide.mutateAsync({ id: taskId, decision: 'resubmit', revise: { payload: { itWorks: input }, uploadIds: newUploadIds } })
      }
    />
  )
}
